import React from 'react';
import { Building2, Users, TrendingUp, Award, MapPin, ArrowRight, Heart, Shield } from 'lucide-react';
import { useLanguage } from '../../../context/LanguageContext';
import entrancePhoto from '../../../photo/entrance.jpg';
import receptionPhoto from '../../../photo/reception.jpg';
import workspacePhoto from '../../../photo/workspace.jpg';
import cityViewPhoto from '../../../photo/city_view.jpg';
import teamPhoto from '../../../photo/team.jpg';
import '../../../styles/sections/Hero.css';

const Hero = ({ scrollToSection }) => {
  const { t } = useLanguage();

  return (
    <section id="home" className="hero-section">
      {/* Photo Collage Background (Right Side) */}
      <div className="hero-photo-collage">
        {/* Photo 1: Office Entrance (Large - takes 2 rows) */}
        <div className="office-photo office-photo-1">
          <img src={entrancePhoto} alt="Office Entrance" />
          <div className="photo-label">
            <Building2 className="h-4 w-4" />
            <span>{t('hero.photoLabels.entrance')}</span>
          </div>
        </div>

        {/* Photo 2: Reception Area */}
        <div className="office-photo office-photo-2">
          <img src={receptionPhoto} alt="Reception Area" />
          <div className="photo-label">
            <Users className="h-4 w-4" />
            <span>{t('hero.photoLabels.reception')}</span>
          </div>
        </div>

        {/* Photo 3: Workspace */}
        <div className="office-photo office-photo-3">
          <img src={workspacePhoto} alt="Workspace" />
          <div className="photo-label">
            <TrendingUp className="h-4 w-4" />
            <span>{t('hero.photoLabels.workspace')}</span>
          </div>
        </div>

        {/* Photo 4: City View */}
        <div className="office-photo office-photo-4">
          <img src={cityViewPhoto} alt="City View" />
          <div className="photo-label">
            <MapPin className="h-4 w-4" />
            <span>{t('hero.photoLabels.view')}</span>
          </div>
        </div>

        {/* Photo 5: Team */}
        <div className="office-photo office-photo-5">
          <img 
            src={teamPhoto} 
            alt="Our Team" 
            style={{ objectFit: 'cover', objectPosition: 'center 65%' }} 
          />
          <div className="photo-label">
            <Heart className="h-4 w-4" />
            <span>{t('hero.photoLabels.team')}</span>
          </div>
        </div>
      </div>

      {/* Decorative floating circles */}
      <div className="hero-decorative-circle-1"></div>
      <div className="hero-decorative-circle-2"></div>

      {/* Content on Left (overlaying the design) */}
      <div className="hero-content-wrapper">
        <div className="hero-content-area">
          {/* Decorative accent line */}
          <div className="hero-accent-line"></div>

          <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold text-white mb-6 leading-tight drop-shadow-2xl">
            {t('hero.title')}
            <span className="block text-brand-light mt-2">{t('hero.titleHighlight')}</span>
          </h1>

          <p className="text-lg md:text-xl text-gray-200 mb-8 leading-relaxed drop-shadow-lg">
            {t('hero.description')}
          </p>

          <div className="flex flex-col sm:flex-row gap-4 mb-6">
            <button
              onClick={() => scrollToSection('about')}
              className="bg-brand-primary text-white px-8 py-4 rounded-lg font-semibold hover:bg-brand-dark transition-all transform hover:scale-105 flex items-center justify-center gap-2 shadow-xl"
            >
              {t('hero.discoverMore')} <ArrowRight className="h-5 w-5" />
            </button>
            <button
              onClick={() => scrollToSection('contact')}
              className="bg-white bg-opacity-10 backdrop-blur-sm border-2 border-white border-opacity-30 text-white px-8 py-4 rounded-lg font-semibold transition-all hover:bg-white hover:bg-opacity-20 shadow-lg"
            >
              {t('hero.contactUs')}
            </button>
          </div>

          {/* Establishment badge - below buttons */}
          <div className="mb-10">
            <div className="bg-brand-light bg-opacity-15 text-white px-5 py-2 rounded-full inline-flex items-center gap-2 text-sm font-semibold border border-white border-opacity-20 shadow-lg backdrop-blur-sm">
              <Award className="h-4 w-4" />
              <span>{t('hero.established')}</span>
            </div>
          </div>

          {/* Enhanced stats grid */}
          <div className="grid grid-cols-3 gap-3">
            <div className="hero-stats-card text-center">
              <div className="flex items-center justify-center mb-2">
                <Users className="h-5 w-5 text-brand-light" />
              </div>
              <div className="text-3xl font-bold text-white mb-1">180+</div>
              <div className="text-xs text-gray-300">{t('hero.stats.expertTeam')}</div>
            </div>
            <div className="hero-stats-card text-center">
              <div className="flex items-center justify-center mb-2">
                <TrendingUp className="h-5 w-5 text-brand-light" />
              </div>
              <div className="text-3xl font-bold text-white mb-1">15+</div>
              <div className="text-xs text-gray-300">{t('hero.stats.globalMarkets')}</div>
            </div>
            <div className="hero-stats-card text-center">
              <div className="flex items-center justify-center mb-2">
                <Shield className="h-5 w-5 text-brand-light" />
              </div>
              <div className="text-3xl font-bold text-white mb-1">24/7</div>
              <div className="text-xs text-gray-300">{t('hero.stats.support')}</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;